import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";

interface DateSeparatorProps {
    date: Date;
    collapsed: boolean;
    onToggle: () => void;
    messageCount: number;
}

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    );
}

function formatDateLabel(date: Date, locale: string, t: (key: string) => string): string {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    if (isSameDay(date, today)) return t("dateSeparator.today");
    if (isSameDay(date, yesterday)) return t("dateSeparator.yesterday");
    return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(date);
}

export function DateSeparator({ date, collapsed, onToggle, messageCount }: DateSeparatorProps) {
    const { t, i18n } = useTranslation("chat");
    const label = formatDateLabel(date, i18n.language, t);

    return (
        <div className="flex w-full items-center gap-3 py-4 select-none">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-zinc-800" />
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={!collapsed}
                aria-label={t("dateSeparator.toggleAria", { date: label })}
                className="flex items-center gap-1.5 rounded-full border border-zinc-800/80 bg-zinc-900/40 px-3 py-1 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200"
            >
                <span>{label}</span>
                {collapsed && messageCount > 0 && <span className="text-zinc-500">· {messageCount}</span>}
                <ChevronDown
                    className={cn("size-3.5 text-zinc-500 transition-transform", collapsed && "-rotate-90")}
                    aria-hidden
                />
            </button>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-zinc-800" />
        </div>
    );
}
